import { useEffect, useState } from "react"
import { Save } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AREAS, type AreaKey } from "@/lib/areas"
import { fetchAreaAccess, saveAreaAccess, type AreaAccessInfo } from "@/lib/api"
import { cn } from "@/lib/utils"

/**
 * «Acesso por área» — que equipas do Koortex entram em cada área do menu
 * inicial. Só admins (Cargo). Quem não está em nenhuma equipa mapeada fica
 * com o Simulador; os admins veem tudo.
 */
export function AreaAccessTab({ shell }: { shell: AppShell | null }) {
  const [info, setInfo] = useState<AreaAccessInfo | null>(null)
  const [draft, setDraft] = useState<Record<string, string[]>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!shell) return
    fetchAreaAccess(shell)
      .then((r) => {
        setInfo(r)
        setDraft(r.area_teams)
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [shell])

  const toggle = (area: AreaKey, slug: string) =>
    setDraft((d) => {
      const cur = d[area] ?? []
      return {
        ...d,
        [area]: cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug],
      }
    })

  const save = async () => {
    if (!shell) return
    setSaving(true)
    try {
      const r = await saveAreaAccess(shell, draft)
      setDraft(r.area_teams)
      shell.bridge.toast({ variant: "success", title: "Acesso por área guardado" })
    } catch (e) {
      shell.bridge.toast({
        variant: "error",
        title: "Erro ao guardar",
        description: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setSaving(false)
    }
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!info) return <p className="text-sm text-muted-foreground">A carregar…</p>

  const areas = AREAS.filter((a) => a.enabled && info.areas.includes(a.key))
  const teams = info.teams ?? []
  // Slugs configurados que já não existem como equipa no tenant.
  const known = new Set(teams.map((t) => t.slug ?? String(t.id)))

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Acesso por área</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Escolhe as equipas do Koortex que entram em cada área. Os
            administradores veem todas. Quem não está em nenhuma equipa
            escolhida fica com o Simulador.
          </p>
        </div>
        <Button size="sm" onClick={save} disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? "A guardar…" : "Guardar"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {info.teams === null && (
          <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            Não foi possível ler as equipas do Koortex. A app tem de ser
            reinstalada para ganhar a permissão <code>teams.read</code>.
          </p>
        )}
        {areas.map((a) => {
          const selected = draft[a.key] ?? []
          const missing = selected.filter((s) => !known.has(s))
          return (
            <div key={a.key}>
              <div className="mb-2 text-sm font-medium">{a.label}</div>
              <div className="flex flex-wrap gap-2">
                {teams.map((t) => {
                  const slug = t.slug ?? String(t.id)
                  const on = selected.includes(slug)
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => toggle(a.key, slug)}
                      className={cn(
                        "rounded-full border px-3 py-1 text-sm transition-colors",
                        on
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border text-muted-foreground hover:bg-muted",
                        !t.is_active && "opacity-60",
                      )}
                    >
                      {t.name}
                      <span className="ml-1 text-xs opacity-70">({t.members})</span>
                    </button>
                  )
                })}
                {missing.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggle(a.key, s)}
                    title="Esta equipa não existe no Koortex — cria-a ou remove-a daqui"
                    className="rounded-full border border-dashed border-amber-400 px-3 py-1 text-sm text-amber-700"
                  >
                    {s} · não existe
                  </button>
                ))}
                {teams.length === 0 && missing.length === 0 && (
                  <span className="text-sm text-muted-foreground">Sem equipas.</span>
                )}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
